import React from "react";
import { Button, Card, Descriptions, Divider, List, Space, Typography } from "antd";

import type { PanchangamFolderFile, PanchangamHistoryEvent } from "../types";

const withCacheBust = (url: string): string => {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}cache_bust=${Date.now()}`;
};

const getVersionLabel = (url: string, index: number): string => {
  const match = url.match(/_v(\d+)\.json(?:\?|$)/i);
  return match ? `v${match[1]}` : `Version ${index + 1}`;
};

interface Props {
  file: PanchangamFolderFile;
  latestVersion: number;
  deployedVersion: number | null;
  isAdmin: boolean;
  onOpenEditor: () => void;
  onOpenAdvancedEditor: () => void;
  onCreateVersion: () => void;
  onCreateVersionFromPrevious: (url: string) => void;
  onOpenDeployDiff: () => void;
  onUndeploy: () => void;
  onDelete: () => void;
  loadingAction?: boolean;
}

const FileDetailsPanel: React.FC<Props> = ({
  file,
  latestVersion,
  deployedVersion,
  isAdmin,
  onOpenEditor,
  onOpenAdvancedEditor,
  onCreateVersion,
  onCreateVersionFromPrevious,
  onOpenDeployDiff,
  onUndeploy,
  onDelete,
  loadingAction,
}) => {
  const allHistory = [...file.meta_data.history].reverse();

  const versionHistory = allHistory.filter((entry) => entry.created_version != null);
  const deploymentHistory = allHistory.filter(
    (entry) => entry.event_type === "deployed" || entry.event_type === "undeployed",
  );

  const renderEvent = (entry: PanchangamHistoryEvent) => {
    return `${entry.event_type.toUpperCase()} • created v${entry.created_version ?? "-"} • deployed v${entry.deployed_version ?? "-"} • ${entry.user_name} (${entry.email}) • ${new Date(entry.event_ts).toLocaleString()}`;
  };

  return (
    <Card title={file.id} extra={<Typography.Text type="secondary">ID: {file.id}</Typography.Text>}>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Stable fetch URL" span={2}>
          <Typography.Text copyable={{ text: withCacheBust(file.static_url) }}>{file.static_url}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Latest version">{latestVersion || "-"}</Descriptions.Item>
        <Descriptions.Item label="Deployed version">
          {deployedVersion == null ? "Not deployed" : deployedVersion}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <Typography.Title level={5}>Previous Version URLs</Typography.Title>
      <List
        size="small"
        bordered
        dataSource={[...file.meta_data.previous_version].reverse()}
        locale={{ emptyText: "No previous versions" }}
        renderItem={(url, index) => (
          <List.Item style={{ minWidth: 0 }}>
            <Space
              size={8}
              style={{
                width: "100%",
                minWidth: 0,
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Space size={8} style={{ minWidth: 0, flex: 1, alignItems: "flex-start" }}>
                <Typography.Text strong style={{ flex: "0 0 auto" }}>
                  {getVersionLabel(url, index)}
                </Typography.Text>
                <Typography.Text
                  copyable={{ text: withCacheBust(url) }}
                  style={{ minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}
                >
                  {url}
                </Typography.Text>
              </Space>
              {isAdmin ? (
                <Button
                  size="small"
                  style={{ flex: "0 0 auto" }}
                  onClick={() => onCreateVersionFromPrevious(url)}
                  loading={loadingAction}
                >
                  Create Version
                </Button>
              ) : null}
            </Space>
          </List.Item>
        )}
      />

      <Divider />

      <Space wrap>
        <Button onClick={onOpenEditor}>Open Safe Editor</Button>
        {isAdmin ? <Button onClick={onOpenAdvancedEditor}>Open Advanced JSON Mode</Button> : null}
        <Button type="primary" onClick={onCreateVersion} loading={loadingAction}>
          Create Version
        </Button>
        {isAdmin ? <Button onClick={onOpenDeployDiff} loading={loadingAction}>Deploy Latest</Button> : null}
        {isAdmin ? <Button danger onClick={onUndeploy} loading={loadingAction}>Undeploy</Button> : null}
        {isAdmin ? <Button danger type="primary" onClick={onDelete} loading={loadingAction}>Delete File</Button> : null}
      </Space>

      {isAdmin ? (
        <>
          <Divider />

          <Typography.Title level={5}>Version History</Typography.Title>
          <List
            size="small"
            bordered
            dataSource={versionHistory}
            locale={{ emptyText: "No version history" }}
            renderItem={(entry) => <List.Item>{renderEvent(entry)}</List.Item>}
          />

          <Divider />

          <Typography.Title level={5}>Deployment History</Typography.Title>
          <List
            size="small"
            bordered
            dataSource={deploymentHistory}
            locale={{ emptyText: "No deployment history" }}
            renderItem={(entry) => <List.Item>{renderEvent(entry)}</List.Item>}
          />

        </>
      ) : null}
    </Card>
  );
};

export default FileDetailsPanel;
